import * as React from 'react'
import { RequirementsToClose } from '@user/components/RequirementsToClose'
import { ParentHeader } from './ParentHeader'
import { ENTITY_TYPES } from '@sales-center/common'
import { ContentRight } from '@user/components/EntityCards/atoms/ContentRight'
import { EntityCardBody } from '@user/components/EntityCards/Body'
import { EntityCardDrawer } from '@user/components/EntityCards/Drawer'
import { EntityCardFooter } from '@user/components/EntityCards/Footer'
import { EntityCardHeader } from '@user/components/EntityCards/Header'
import { EntityCardWrapper } from '@user/components/EntityCards/Wrapper'
import { EntityName } from '@user/components/EntityCards/atoms/EntityName'
import { EntityPrimaryContactDrawer } from '@user/components/EntityCards/atoms/EntityPrimaryContactDrawer'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { LEAD_STATUSES, LeadCardProps, LeadStatus } from './typings'
import { useSelectFunnelStagesByType } from '@user/store/salesFunnel/hooks'
import { Appointments } from '@user/components/Scheduling/Estimates'
import { LocationsPopoverButton } from '@user/components/EntityCards/Locations'
import { SchedulingWizardContextProvider } from '@user/components/Scheduling'
import { useSchedulingVisibility } from '@user/store/closingRequirements'
import { useToggleBoolean } from '@user/utils/useToggleBoolean'
import { CWQDialogContextProvider } from '@user/components/SoldServices'
import LocationContextProvider from '@user/components/EntityCards/Locations/LocationsPopoverButton/context/LocationContextProvider'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { getLeadList } from '@user/store/lead'
import { LeadCompanyRequiredFieldsBanner } from '@user/features/LeadCompanyRequiredFields/LeadCompanyRequiredFieldsBanner'
import { LeadCompanyRequiredFieldsProvider } from '@user/features/LeadCompanyRequiredFields/LeadCompanyRequiredFieldsContext'
import {
  MostRecentKnockStatus,
  getKnockStatusFromCard,
} from '@user/features/MapView/MostRecentKnockStatus'

export function LeadCard(props: LeadCardProps) {
  const {
    lead,
    lead: {
      services,
      closedDate,
      address,
      branchId,
      closingRequirements,
      externalLocationId,
      id,
      primaryContact,
      salesFunnelId,
      servicesCount,
      salesFunnelStageId,
      leadName,
      referralDiscountId,
      scheduleSettings,
      hasValidLocationAddress,
      hasValidPropertyAddress,
    },
    canUpdate,
    hasScheduledAppointment,
  } = props

  const stages = useSelectFunnelStagesByType(salesFunnelId, 'Lead')

  const currentStage = React.useMemo(
    () => stages.filter((s) => s.id == salesFunnelStageId).shift(),
    [salesFunnelStageId, stages],
  )

  const leadStatus = React.useMemo((): LeadStatus => {
    if (closedDate == null || !currentStage) return LEAD_STATUSES.Open

    const { name } = currentStage

    switch (name) {
      case 'Disqualified':
        return LEAD_STATUSES.Disqualified
      case 'Qualify':
        return LEAD_STATUSES.Qualify
      default:
        return LEAD_STATUSES.Open
    }
  }, [currentStage, closedDate])

  const schedulingVisible = useSchedulingVisibility(scheduleSettings)

  const showApptChip =
    leadStatus !== LEAD_STATUSES.Disqualified &&
    hasScheduledAppointment &&
    schedulingVisible

  const [open, toggleOpen] = useToggleBoolean(false)

  const dispatch = useAppDispatch()
  const refreshLeads = React.useCallback(() => {
    dispatch(getLeadList())
  }, [dispatch])

  return (
    <LeadCompanyRequiredFieldsProvider lead={lead} onRefresh={refreshLeads}>
      <CWQDialogContextProvider>
        <SchedulingWizardContextProvider
          entityId={id}
          entityType={ENTITY_TYPES.Lead}
          salesFunnelId={salesFunnelId}
        >
          <LocationContextProvider
            entityId={id}
            entityType={ENTITY_TYPES.Lead}
            locations={[]}
          >
            <LeadCompanyRequiredFieldsBanner variant="card" />
            <MostRecentKnockStatus
              variant="card"
              knockStatus={getKnockStatusFromCard(
                lead.knockStatus,
                lead.knockStatusReason,
              )}
            />
            <EntityCardWrapper>
              <EntityCardHeader>
                <ParentHeader {...props} />
              </EntityCardHeader>

              <EntityCardBody>
                <Box display="flex" justifyContent="space-between">
                  <Box>
                    <EntityName
                      label="Contact"
                      name={leadName}
                      entityId={id}
                      detailUrl={`/leads/${id}`}
                    />
                    <Stack direction="row" alignItems="flex-start">
                      <LocationsPopoverButton
                        buttonStyle="chip"
                        entityId={id}
                        entityType={ENTITY_TYPES.Lead}
                        entityBranchId={branchId}
                        canUpdateEntity={canUpdate}
                        matched={lead.externalLocationCode !== null}
                        variant="entity"
                      />
                      {showApptChip && (
                        <Box ml={1} mt={1.4}>
                          <Appointments
                            entityId={id}
                            entityType={ENTITY_TYPES.Lead}
                          />
                        </Box>
                      )}
                    </Stack>
                  </Box>

                  <ContentRight
                    detail={lead}
                    entityId={id}
                    entityStatus={leadStatus}
                    entityType={ENTITY_TYPES.Lead}
                  />
                </Box>

                <EntityPrimaryContactDrawer
                  canUpdate={canUpdate}
                  contact={primaryContact}
                  entityId={id}
                  entityType={ENTITY_TYPES.Lead}
                  hasValidPropertyAddress={hasValidPropertyAddress}
                />
              </EntityCardBody>

              <EntityCardFooter
                handleOpenDrawer={toggleOpen}
                schedulingVisible={schedulingVisible}
                isOpen={open}
                entityId={id}
                entityType={ENTITY_TYPES.Lead}
                entityDetails={{
                  services,
                  closedDate,
                  servicesCount,
                  status: leadStatus,
                  closingRequirements,
                }}
              />

              <EntityCardDrawer isOpen={open}>
                <RequirementsToClose
                  address={address}
                  services={services}
                  branchId={branchId}
                  entityId={id}
                  entityType={ENTITY_TYPES.Lead}
                  externalLocationId={externalLocationId}
                  externalBillToId={lead.billToContact.externalLinkId}
                  leadStatus={leadStatus}
                  salesFunnelId={salesFunnelId}
                  servicesCount={servicesCount}
                  canUpdate={canUpdate}
                  referralDiscountId={referralDiscountId}
                  requirementsAndCounts={closingRequirements}
                  hasValidLocationAddress={hasValidLocationAddress}
                  scheduleSettings={scheduleSettings}
                />
              </EntityCardDrawer>
            </EntityCardWrapper>
          </LocationContextProvider>
        </SchedulingWizardContextProvider>
      </CWQDialogContextProvider>
    </LeadCompanyRequiredFieldsProvider>
  )
}
